import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch {
      toast.error("Failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!result?.token) return;
    const url = `${window.location.origin}/reset-password/${result.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Reset link copied");
    } catch {
      toast.error("Couldn't copy — select manually");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <div className="px-6 sm:px-12 py-6">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-back">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t("auth.back_to_login")}
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          {!result ? (
            <>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">/ Reset</div>
              <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter mb-2">{t("auth.reset_password")}</h1>
              <p className="text-muted-foreground mb-10">Enter your email to get a reset token.</p>
              <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{t("auth.email")}</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 bg-muted border-border" placeholder="you@somewhere.com" data-testid="forgot-email-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 brand-bg text-black font-semibold hover:opacity-90 active:scale-[0.98] transition" data-testid="forgot-submit-btn">
                  {loading ? t("common.loading") : t("auth.reset_password")}
                </Button>
              </form>
            </>
          ) : (
            <div className="rounded-2xl border border-border bg-card/40 p-8 tracing-beam" data-testid="forgot-success">
              <div className="h-12 w-12 rounded-full brand-bg text-black flex items-center justify-center mb-5">
                <Check className="h-6 w-6" strokeWidth={2} />
              </div>
              <h2 className="font-heading text-3xl tracking-tight mb-2">{t("auth.reset_link_sent")} ✓</h2>
              <p className="text-sm text-muted-foreground mb-6">
                {result.token
                  ? "Since email isn't wired yet, here's your reset link — tap to open or copy it:"
                  : "If that email is registered, a reset link has been prepared."}
              </p>
              {result.token && (
                <div className="space-y-3">
                  <Link
                    to={`/reset-password/${result.token}`}
                    className="block w-full h-12 rounded-md brand-bg text-black font-semibold flex items-center justify-center hover:opacity-90 active:scale-[0.98] transition"
                    data-testid="forgot-open-link"
                  >
                    Open reset link
                  </Link>
                  <button
                    onClick={copy}
                    className="w-full h-10 rounded-md border border-border bg-muted text-muted-foreground hover:text-foreground text-xs font-mono flex items-center justify-center gap-2"
                    data-testid="forgot-copy-link"
                  >
                    <Copy className="h-3.5 w-3.5" strokeWidth={1.5} /> Copy link
                  </button>
                </div>
              )}
              <Link to="/login" className="block text-center text-sm brand-text hover:underline mt-6" data-testid="forgot-back-login">
                {t("auth.back_to_login")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
